

import { Suspense } from 'react';
import type { ReactNode } from 'react';
import {
  SignOutIcon,
} from '@phosphor-icons/react'
import { Outlet } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { TauriTitleBarShell } from '@/components/layout/TauriTitleBarShell';
import { Button } from '@/components/ui/button';
import { handleLogout } from '@/utils/auth/auth-utils';
import { Loading } from '@/components/ui/loading';
import { ModeToggle } from '@/components/mode-toggle';
import { TooltipProvider } from '@/components/ui/tooltip';
import { isTauri } from '@/lib/utils/tauri';
// Protected Layout component that applies MainLayout to all protected routes
export function ProtectedLayout() {
return (
    <ProtectedRoute>
      <MainLayout>
        <div className="will-change-transform h-full">
          <Outlet />
        </div>
      </MainLayout>
    </ProtectedRoute>
  );
}

export function AuthRouteLayout() {
  return (
    <TauriTitleBarShell className="min-h-screen w-full">
      <Outlet />
    </TauriTitleBarShell>
  );
}

// Client portal route shell without org navigation
export function ClientPortalRouteShell() {
  return (
    <ProtectedRoute>
      <TooltipProvider delayDuration={0}>
        <Outlet />
      </TooltipProvider>
    </ProtectedRoute>
  );
}

// Simple Layout for setup flows without sidebar - with beautiful animated background
export function SetupLayout() {
  const isDesktopApp = isTauri();

  return (
    <ProtectedRoute>
      <TauriTitleBarShell>
        <div className="h-[var(--tauri-content-height,100vh)] w-full relative bg-background overflow-hidden">
        {/* Dark Mode Toggle - positioned in top-right, z-50 to be above TauriDragRegion */}
        {/* Only show for web since Tauri has it in the title bar */}
        {!isDesktopApp && (
          <div className="absolute top-4 right-4 z-50">
            <ModeToggle />
          </div>
        )}
        
        {/* Modern gradient background using theme colors with brand accents */}
        <div className="absolute inset-0 bg-gradient-to-br from-primary/12 via-muted/8 via-accent/6 to-primary/18 animate-gradient-slow dark:from-primary/20 dark:via-muted/15 dark:via-accent/12 dark:to-primary/25"></div>
        
        {/* Subtle brand color accent layers */}
        <div className="absolute inset-0 bg-gradient-to-tr from-[hsl(var(--brand-blue))]/8 via-transparent via-[hsl(var(--brand-green))]/6 to-[hsl(var(--brand-orange))]/8 animate-pulse-slow dark:from-[hsl(var(--brand-blue))]/12 dark:via-transparent dark:via-[hsl(var(--brand-green))]/8 dark:to-[hsl(var(--brand-orange))]/12"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-[hsl(var(--brand-red))]/6 via-[hsl(var(--brand-yellow))]/8 to-transparent animate-gradient-slow animation-delay-3000 dark:from-[hsl(var(--brand-red))]/10 dark:via-[hsl(var(--brand-yellow))]/12 dark:to-transparent"></div>
        
        {/* Modern overlay for clean contrast */}
        <div className="absolute inset-0 bg-background/60 dark:bg-background/70"></div>
        
        {/* Modern subtle flowing shapes using theme and brand colors */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden opacity-25 dark:opacity-20">
          {/* Large organic shapes with modern opacity */}
          <div className="absolute top-[10%] left-[10%] w-96 h-96 rounded-full bg-gradient-to-br from-primary/20 to-muted/12 mix-blend-overlay animate-blob animation-delay-1000 animate-float-gentle dark:from-primary/25 dark:to-muted/18"></div>
          <div className="absolute bottom-[15%] right-[15%] w-80 h-80 rounded-full bg-gradient-to-tr from-[hsl(var(--brand-orange))]/18 to-[hsl(var(--brand-yellow))]/12 mix-blend-overlay animate-blob animation-delay-3000 animate-float-reverse dark:from-[hsl(var(--brand-orange))]/22 dark:to-[hsl(var(--brand-yellow))]/16"></div>
          <div className="absolute top-[50%] left-[20%] w-72 h-72 rounded-full bg-gradient-to-bl from-[hsl(var(--brand-green))]/18 to-accent/12 mix-blend-overlay animate-blob animation-delay-5000 animate-float-slow dark:from-[hsl(var(--brand-green))]/22 dark:to-accent/16"></div>
          <div className="absolute top-[20%] right-[5%] w-64 h-64 rounded-full bg-gradient-to-br from-[hsl(var(--brand-red))]/18 to-destructive/12 mix-blend-overlay animate-blob animation-delay-2000 animate-drift-horizontal dark:from-[hsl(var(--brand-red))]/22 dark:to-destructive/16"></div>
          <div className="absolute bottom-[40%] left-[60%] w-56 h-56 rounded-full bg-gradient-to-tl from-[hsl(var(--brand-blue))]/18 to-primary/12 mix-blend-overlay animate-blob animation-delay-4000 animate-drift-vertical dark:from-[hsl(var(--brand-blue))]/22 dark:to-primary/16"></div>
          
          {/* Medium abstract shapes with refined opacity */}
          <div className="absolute top-[70%] right-[25%] w-48 h-48 rounded-full bg-gradient-to-r from-secondary/15 to-muted/10 mix-blend-overlay animate-blob animation-delay-6000 animate-float-gentle dark:from-secondary/20 dark:to-muted/15"></div>
          <div className="absolute top-[35%] left-[70%] w-40 h-40 rounded-full bg-gradient-to-l from-accent/15 to-primary/10 mix-blend-overlay animate-blob animation-delay-7000 animate-float-reverse dark:from-accent/20 dark:to-primary/15"></div>
          
          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiNmZmZmZmYwMyIgZD0iTTAgMGg2MHY2MEgweiIvPjxwYXRoIGQ9Ik0zNiAxOGgtMnYyNGgyVjE4em0tMTAgMGgydjI0aC0yVjE4ek0xOCAzNnYtMmgyNHYyaC0yNHptMC0xMGgyNHYyaC0yNHYtMnoiIGZpbGw9IiNmZmZmZmYwOCIvPjwvZz48L3N2Zz4=')] opacity-10 dark:opacity-15"></div>
        </div>
        
        {/* Modern subtle floating particles using theme colors */}
        <div className="absolute inset-0 overflow-hidden opacity-60 dark:opacity-50">
          <div className="particle bg-primary/12 top-[15%] left-[45%] dark:bg-primary/18"></div>
          <div className="particle bg-[hsl(var(--brand-orange))]/12 top-[35%] left-[20%] animation-delay-2000 dark:bg-[hsl(var(--brand-orange))]/18"></div>
          <div className="particle bg-[hsl(var(--brand-green))]/12 top-[70%] left-[60%] animation-delay-4000 dark:bg-[hsl(var(--brand-green))]/18"></div>
          <div className="particle bg-[hsl(var(--brand-red))]/12 top-[20%] left-[80%] animation-delay-1000 dark:bg-[hsl(var(--brand-red))]/18"></div>
          <div className="particle bg-accent/12 top-[60%] left-[10%] animation-delay-3000 dark:bg-accent/18"></div>
          <div className="particle bg-[hsl(var(--brand-yellow))]/12 top-[85%] left-[25%] animation-delay-2500 dark:bg-[hsl(var(--brand-yellow))]/18"></div>
          <div className="particle bg-secondary/12 top-[40%] left-[70%] animation-delay-1500 dark:bg-secondary/18"></div>
          <div className="particle bg-[hsl(var(--brand-blue))]/12 top-[15%] left-[30%] animation-delay-3500 dark:bg-[hsl(var(--brand-blue))]/18"></div>
          <div className="particle bg-primary/10 top-[50%] left-[85%] animation-delay-5000 dark:bg-primary/15"></div>
          <div className="particle bg-[hsl(var(--brand-orange))]/10 top-[75%] left-[5%] animation-delay-6000 dark:bg-[hsl(var(--brand-orange))]/15"></div>
        </div>

        {/* Main Content - relative positioned above the backgrounds, scrolls independently so the background stays fixed */}
        <main className="w-full h-full overflow-y-auto relative z-10">
          <Outlet />
        </main>

        {/* Sign Out Button - positioned absolutely at bottom right */}
        {/* Only show for web since Tauri has it in the user menu */}
        {!isDesktopApp && (
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              variant="outline"
              onClick={handleLogout}
              className="bg-card/90 backdrop-blur-sm border-border text-muted-foreground hover:text-foreground hover:bg-card/95 hover:border-border/70 rounded-full px-6 shadow-lg transition-all duration-200 text-sm"
            >
              <SignOutIcon className="mr-2 h-4 w-4" />
              Sign Out
            </Button>
          </div>
        )}
        </div>
      </TauriTitleBarShell>
    </ProtectedRoute>
  );
}

// Global loading component - centralized to ensure consistent loading UI
export const AppLoader = ({ inline = false, size = 'lg' }: { inline?: boolean, size?: 'sm' | 'md' | 'lg' }) => {
  if (inline) {
    return (
      <div className="flex items-center justify-center py-4">
        <Loading variant="spinner" size={size} />
      </div>
    );
  }

  // For full-screen loading, use isInitialLoad flag with logo variant
  return (
    <Loading 
      variant="logo" 
      size={size} 
      isInitialLoad={true} 
    />
  );
};

// Suspense wrapper with app loader
interface LazyLoadProps {
  children: ReactNode;
}

export function LazyLoad({ children }: LazyLoadProps) {
  return (
    <Suspense fallback={<AppLoader />}>
      {children}
    </Suspense>
  );
} 
